import React, { useState } from 'react';
import { Table, Button, Pagination, Space, Empty, Tag, Modal, Input, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import DateRangeFilterItem from '../../../delivery/components/DateRangeFilterItem';
import { hasResource } from '../../../../utils/resourceAuth';
import { ResourceCode } from '../../../../constants/resourceCodes';
import { formatCentsAmount, formatYyyymmdd } from '../../../../utils/invoiceAmount';
import { openBillExportFile } from '../../../../utils/billExportDownload';
import InvoiceDueDateModal from '../../../../components/settlement/InvoiceDueDateModal';
import {
    GET_REALTIME_INVOICES,
    GetRealtimeInvoicesResponse,
} from './graphql/queries/getRealtimeInvoices';
import {
    VOID_REALTIME_INVOICE,
    VoidRealtimeInvoiceResponse,
    EXPORT_REALTIME_INVOICE_ORDER_DETAIL,
    ExportRealtimeInvoiceOrderDetailResponse,
} from './graphql/mutations/realtimeInvoiceMutations';
import {
    GET_REALTIME_INVOICE_DETAIL,
    GetRealtimeInvoiceDetailResponse,
} from './graphql/queries/getRealtimeInvoiceDetail';
import { RealtimeInvoiceListItemType } from './graphql/types/RealtimeInvoiceType';
import { todayDateRange } from './config/filterOptions';

interface RealtimeInvoiceListPageProps {
    settlementClientId?: string;
}

const toYyyymmdd = (s: string | null): number | undefined => {
    if (!s) return undefined;
    const n = Number(s.replace(/[-/]/g, ''));
    return Number.isNaN(n) ? undefined : n;
};

const STATUS_COLOR: Record<string, string> = {
    INITIATED: 'processing',
    SUCCESS: 'success',
    FAILED: 'error',
    CANCELLED: 'default',
};

const RealtimeInvoiceListPage: React.FC<RealtimeInvoiceListPageProps> = ({ settlementClientId }) => {
    const { t } = useTranslation();
    const canOperate = hasResource([ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_OPERATE]);
    const canCheck = hasResource([ResourceCode.settlement.SETTLEMENT_REVENUE_BILL_CHECK]);

    const [tempRange, setTempRange] = useState<[string | null, string | null] | null>(todayDateRange());
    const [range, setRange] = useState<[string | null, string | null] | null>(todayDateRange());
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(10);

    const [dueDateTarget, setDueDateTarget] = useState<RealtimeInvoiceListItemType | null>(null);
    const [voidTarget, setVoidTarget] = useState<RealtimeInvoiceListItemType | null>(null);
    const [voidReason, setVoidReason] = useState('');

    const [voidInvoice, { loading: voiding }] = useMutation<VoidRealtimeInvoiceResponse>(VOID_REALTIME_INVOICE);
    const [exportDetailMutation] = useMutation<ExportRealtimeInvoiceOrderDetailResponse>(EXPORT_REALTIME_INVOICE_ORDER_DETAIL);
    const [fetchDetail] = useLazyQuery<GetRealtimeInvoiceDetailResponse>(GET_REALTIME_INVOICE_DETAIL, {
        fetchPolicy: 'network-only',
    });
    const [pdfLoadingId, setPdfLoadingId] = useState<string | null>(null);
    const [exportingId, setExportingId] = useState<string | null>(null);

    const handlePdf = async (invoiceId: string, kind: 'preview' | 'download') => {
        setPdfLoadingId(invoiceId);
        try {
            const { data } = await fetchDetail({ variables: { invoiceId } });
            const detail = data?.getRealtimeInvoiceDetail;
            const url = kind === 'preview' ? detail?.pdfPreviewUrl : detail?.pdfDownloadUrl;
            if (url) {
                openBillExportFile(url);
            } else {
                message.warning(t('settlement.realtimeInvoice.actions.pdfNotReady'));
            }
        } catch (e) {
            message.error((e as Error).message || t('settlement.realtimeInvoice.actions.pdfNotReady'));
        } finally {
            setPdfLoadingId(null);
        }
    };

    const handleExportDetail = async (invoiceId: string) => {
        setExportingId(invoiceId);
        try {
            const { data } = await exportDetailMutation({ variables: { invoiceId } });
            if (data?.exportRealtimeInvoiceOrderDetail) {
                message.success(t('settlement.realtimeInvoice.actions.exportSuccess', '导出任务已提交，请稍后前往下载中心查看'));
            }
        } catch (e) {
            message.error((e as Error).message || t('settlement.realtimeInvoice.actions.exportFailed', '导出失败'));
        } finally {
            setExportingId(null);
        }
    };

    const listQuery = useQuery<GetRealtimeInvoicesResponse>(GET_REALTIME_INVOICES, {
        variables: {
            settlementClientId,
            dateFrom: toYyyymmdd(range?.[0] ?? null) ?? null,
            dateTo: toYyyymmdd(range?.[1] ?? null) ?? null,
            page,
            size,
        },
        skip: !settlementClientId,
        fetchPolicy: 'cache-and-network',
    });
    const result = listQuery.data?.getRealtimeInvoices;
    const rows = result?.content ?? [];
    const total = result?.pageInfo?.totalElements ?? 0;

    const handleFilter = () => {
        setRange(tempRange);
        setPage(1);
    };

    const handleVoid = async () => {
        if (!voidTarget) return;
        if (!voidReason.trim()) {
            message.warning(t('settlement.realtimeInvoice.voidModal.reasonRequired'));
            return;
        }
        try {
            const { data } = await voidInvoice({
                variables: { invoiceId: voidTarget.invoiceId, cancelReason: voidReason.trim() },
            });
            const res = data?.voidRealtimeInvoice;
            if (res?.success) {
                message.success(t('settlement.realtimeInvoice.voidModal.success'));
                setVoidTarget(null);
                setVoidReason('');
                listQuery.refetch();
            } else {
                message.error(res?.message || t('settlement.realtimeInvoice.voidModal.failed'));
            }
        } catch (e) {
            message.error((e as Error).message || t('settlement.realtimeInvoice.voidModal.failed'));
        }
    };

    const columns: ColumnsType<RealtimeInvoiceListItemType> = [
        { title: t('settlement.realtimeInvoice.columns.invoiceNumber'), dataIndex: 'invoiceNo' },
        { title: t('settlement.realtimeInvoice.columns.date'), dataIndex: 'invoiceDate', render: (v: number) => formatYyyymmdd(v) },
        { title: t('settlement.realtimeInvoice.columns.dueDate'), dataIndex: 'dueDate', render: (v: number) => formatYyyymmdd(v) },
        { title: t('settlement.realtimeInvoice.columns.amount'), dataIndex: 'amount', render: (v: number, r) => formatCentsAmount(v, r.currency) },
        {
            title: t('settlement.realtimeInvoice.columns.status'),
            dataIndex: 'status',
            render: (v: string) => <Tag color={STATUS_COLOR[v] ?? 'default'}>{t(`settlement.realtimeInvoice.status.${v}`, v)}</Tag>,
        },
        {
            title: t('settlement.realtimeInvoice.columns.actions'),
            key: 'actions',
            render: (_: unknown, r) => (
                <Space size="small">
                    <Button type="link" size="small" loading={pdfLoadingId === r.invoiceId} disabled={r.status !== 'SUCCESS'} onClick={() => handlePdf(r.invoiceId, 'preview')}>
                        {t('settlement.realtimeInvoice.actions.preview')}
                    </Button>
                    <Button type="link" size="small" loading={pdfLoadingId === r.invoiceId} disabled={r.status !== 'SUCCESS'} onClick={() => handlePdf(r.invoiceId, 'download')}>
                        {t('settlement.realtimeInvoice.actions.download')}
                    </Button>
                    {canCheck && (
                        <Button type="link" size="small" loading={exportingId === r.invoiceId} disabled={r.status !== 'SUCCESS'} onClick={() => handleExportDetail(r.invoiceId)}>
                            {t('settlement.realtimeInvoice.actions.exportDetail', '导出明细')}
                        </Button>
                    )}
                    {canOperate && (
                        <Button type="link" size="small" disabled={r.status !== 'SUCCESS'} onClick={() => setDueDateTarget(r)}>
                            {t('settlement.realtimeInvoice.actions.modifyDueDate')}
                        </Button>
                    )}
                    {canOperate && (
                        <Button type="link" size="small" danger disabled={r.status === 'CANCELLED'} onClick={() => { setVoidTarget(r); setVoidReason(''); }}>
                            {t('settlement.realtimeInvoice.actions.void')}
                        </Button>
                    )}
                </Space>
            ),
        },
    ];

    if (!settlementClientId) {
        return <Empty description={t('settlement.realtimeInvoice.selectClientHint')} />;
    }

    return (
        <div>
            <div className="mb-4 flex items-end gap-4">
                <DateRangeFilterItem
                    label={t('settlement.realtimeInvoice.columns.date')}
                    value={tempRange}
                    onChange={setTempRange}
                    spanClassName="w-[9em]"
                />
                <Button type="primary" onClick={handleFilter}>
                    {t('settlement.realtimeInvoice.actions.filter')}
                </Button>
                <Button
                    className="ml-auto"
                    icon={<ReloadOutlined />}
                    loading={listQuery.loading}
                    onClick={() => listQuery.refetch()}
                >
                    {t('settlement.realtimeInvoice.actions.refresh')}
                </Button>
            </div>

            <Table
                rowKey="invoiceId"
                columns={columns}
                dataSource={rows}
                loading={listQuery.loading}
                pagination={false}
            />

            <div className="mt-4 flex items-center justify-between">
                <div className="text-gray-500">{total} {t('settlement.realtimeInvoice.results')}</div>
                <Pagination
                    current={page}
                    total={total}
                    pageSize={size}
                    size="small"
                    showSizeChanger
                    onChange={(p, s) => {
                        setPage(p);
                        setSize(s);
                    }}
                />
            </div>

            {dueDateTarget && (
                <InvoiceDueDateModal
                    open={!!dueDateTarget}
                    invoiceId={dueDateTarget.invoiceId}
                    currentDueDate={dueDateTarget.dueDate}
                    onCancel={() => setDueDateTarget(null)}
                    onSuccess={() => {
                        setDueDateTarget(null);
                        listQuery.refetch();
                    }}
                />
            )}

            <Modal
                title={t('settlement.realtimeInvoice.voidModal.title')}
                open={!!voidTarget}
                onOk={handleVoid}
                onCancel={() => setVoidTarget(null)}
                confirmLoading={voiding}
                okButtonProps={{ danger: true }}
                okText={t('settlement.realtimeInvoice.actions.void')}
                destroyOnClose
            >
                <p>{t('settlement.realtimeInvoice.voidModal.confirm', { invoiceNo: voidTarget?.invoiceNo ?? '' })}</p>
                <Input.TextArea
                    rows={3}
                    value={voidReason}
                    onChange={(e) => setVoidReason(e.target.value)}
                    placeholder={t('settlement.realtimeInvoice.voidModal.reasonPlaceholder') ?? ''}
                    maxLength={200}
                />
            </Modal>
        </div>
    );
};

export default RealtimeInvoiceListPage;
